/**
 * Die Seite „Entwickler“ — Schmied, Zimmermann und Hüter an EINEM Ort. (16.10.2026; neu gebaut 18.10.2026)
 *
 * Martin (18.10.): „Die Rubrik Entwickler komplett überarbeiten: wir haben jetzt drei Entwickler, die Übersicht soll besser werden.“
 * und „Ganz unten gibt es einen Schalter für Stream oder normal — das betrifft nur den Schmied. Bitte vereinheitlichen.“
 *
 * Aufbau, von oben nach unten:
 *   1. Team      — drei gleich gebaute Zeilen: Name, Rolle in einem Wort, Zustand (Wort + Zeichen), Schalter „im Chat“.
 *   2. Stimme und Ton — je Entwickler eingeklappt: Stimme, Tempo, Ausführlichkeit (+ wie man ihn anspricht).
 *   3. Vorlesen  — gilt für alle: „Entwickler vorlesen“ und „Beiträge in einem Stück vorlesen“ (Stream oder Stück für Stück).
 *   4. Server    — das Server-Netz, getrennt von den Entwicklern.
 *
 * Der Zustand hat für alle drei dieselben Worte (entwickler-zustand.ts): aus · gesperrt · bereit · hört zu.
 * Die Schalter in Zeile 1 sind die Sichtbarkeit im Chat: Schmied = „Entwickler im Chat“ (Hauptschalter, auch oben auf der
 * Startseite der Einstellungen), Zimmermann/Hüter = ihre eigenen Kontoschalter. Zimmermann und Hüter kommen nur herein, wenn der
 * Hauptschalter an ist — ist er aus, zeigen sie „aus“.
 *
 * ⚠️ Alle Felder hier sind „schutz“ (shared/einstellung-aendern.ts GESPERRTE_FELDER): nur Martin selbst, nie auf Zuruf.
 *    Die Felder und ihre Kontowerte sind dieselben wie vorher — nur Ort und Form sind neu.
 * ⚠️ Nur Konto 1 (shared/entwickler-im-chat.ts). Ein anderes Konto bekommt nur einen Satz.
 * ⚠️ Alles Angezeigte sind feste Worte (textContent) — nie ein Text aus dem Server.
 */
import { ENTWICKLER_GESAMT_FELD, ENTWICKLER_IM_CHAT_FELD, entwicklerGesamtAn, entwicklerImChatAn, entwicklerImChatFuerKonto } from "../../../shared/entwickler-im-chat.js";
import { entwickler, type EntwicklerId, type ZusatzId } from "../../../shared/entwickler.js";
import {
  AUSFUEHRLICHKEITEN,
  AUSFUEHRLICHKEIT_VORGABE,
  ENTWICKLER_TEMPO_GRENZE,
  ENTWICKLER_VORLESEN_FELD,
  SCHMIED_GANZ_FELD,
  SCHMIED_STIMMEN,
  ausfuehrlichkeitAus,
  entwicklerStimmeAus,
  entwicklerTempoAus,
  entwicklerVorlesenAn,
  zusatzVorlesenAn,
  ZUSATZ_VORLESEN_FELD,
  schmiedStimmeGanzAn,
} from "../../../shared/schmied.js";
import { SERVER_NETZ_FELD } from "../../../shared/server-netz.js";
import { einklappbar, einstAbschnitt, einstMehr, einstSchalter, einstText, schalterKnopf } from "./einst-bausteine.js";
import { ROLLE, TEAM_IDS, ZUSTAND_WORT, entwicklerZustand, teamName, zuletztText, zustandsZeichen, type EntwicklerZustand } from "./entwickler-zustand.js";

/** `hoert` (18.10.2026): ist ein Hörer verbunden — fehlt bei einem älteren Server (dann kennt die Seite nur „bereit“). */
export interface ZusatzStand {
  notschalter: boolean;
  zugang: boolean;
  hoert?: boolean;
  /** 18.10.2026: Sekunden seit dem letzten Hörer-Kontakt; `null`: seit dem Start nie. */
  zuletztGesehenS?: number | null;
}

/** Was der Server über das Team sagt — nur Booleans (server/entwickler-status-routen.ts). */
export interface TeamStand {
  schmiedHoertMit: boolean;
  /** 18.10.2026: dasselbe für den Schmied (fehlt bei einem älteren Server). */
  schmiedZuletztGesehenS?: number | null;
  zimmermann: ZusatzStand;
  /** 17.10.2026: der Hüter (Server 7) — fehlt bei einem älteren Server; dann gilt sein Stand als nicht abrufbar. */
  hueter?: ZusatzStand;
}

export type ProbeZustand = "idle" | "loading" | "playing";

export interface EntwicklerSeiteKontext {
  /** Der Stand der Einstellungen (roh). */
  werte: () => Record<string, unknown>;
  uid: number | null | undefined;
  /** Schreibt die Felder (wie die übrigen Seiten) und meldet den Satz, sobald gespeichert. */
  speichere: (felder: Record<string, unknown>, satz: string) => void;
  /** Spielt eine Hörprobe mit dieser Stimme und diesem Tempo; `zeige` folgt dem Zustand. */
  spieleProbe: (stimme: string, tempo: number, zeige: (z: ProbeZustand) => void) => void;
  stoppeProbe: () => void;
  /** Für Tests ersetzbar: den Stand vom Server holen (`null`: nicht abrufbar). */
  holeStand?: () => Promise<TeamStand | null>;
}

/** Einen Zusatz-Stand streng lesen: nur Booleans; `hoert` darf fehlen. */
function zusatzStandAus(roh: unknown): ZusatzStand | null {
  const z = roh as { notschalter?: unknown; zugang?: unknown; hoert?: unknown } | null | undefined;
  if (!z || typeof z !== "object" || typeof z.notschalter !== "boolean" || typeof z.zugang !== "boolean") return null;
  const w = (z as { zuletztGesehenS?: unknown }).zuletztGesehenS;
  const zuletzt = w === null ? { zuletztGesehenS: null } : typeof w === "number" && Number.isFinite(w) && w >= 0 ? { zuletztGesehenS: Math.floor(w) } : {};
  return { notschalter: z.notschalter, zugang: z.zugang, ...(typeof z.hoert === "boolean" ? { hoert: z.hoert } : {}), ...zuletzt };
}

/** Streng lesen, was der Server schickt: nur genau diese Form mit Booleans, sonst `null`. Rein. */
export function teamStandAus(roh: unknown): TeamStand | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as { schmiedHoertMit?: unknown; schmiedZuletztGesehenS?: unknown; zimmermann?: unknown; hueter?: unknown };
  const z = zusatzStandAus(r.zimmermann);
  if (typeof r.schmiedHoertMit !== "boolean" || !z) return null;
  // Der Hüter nur in genau dieser Form — sonst fehlt er (nicht abrufbar), der Rest bleibt gültig.
  const hueter = zusatzStandAus(r.hueter);
  const sz = r.schmiedZuletztGesehenS;
  const schmiedZuletzt = sz === null ? { schmiedZuletztGesehenS: null } : typeof sz === "number" && Number.isFinite(sz) && sz >= 0 ? { schmiedZuletztGesehenS: Math.floor(sz) } : {};
  return { schmiedHoertMit: r.schmiedHoertMit, ...schmiedZuletzt, zimmermann: z, ...(hueter ? { hueter } : {}) };
}

async function standVomServer(): Promise<TeamStand | null> {
  try {
    const r = await fetch("/api/entwickler/status", { credentials: "same-origin" });
    return r.ok ? teamStandAus(await r.json()) : null;
  } catch {
    return null;
  }
}

/** Der Stand eines Zusatz-Entwicklers aus dem Team-Stand — `null`, wenn er fehlt. Rein. */
function zusatzStand(stand: TeamStand | null, id: ZusatzId): ZusatzStand | null {
  return stand ? (stand[id] ?? null) : null;
}

/** Hat der Server diesen Zusatz-Entwickler gesperrt? Ohne Stand: nein (unbekannt ist nicht gesperrt). Rein. */
export function zusatzGesperrt(stand: TeamStand | null, id: ZusatzId): boolean {
  const s = zusatzStand(stand, id);
  return !!s && !(s.notschalter && s.zugang);
}

/** Warum der Server ihn gesperrt hat — ein kurzer fester Satz; `""`, wenn er nicht gesperrt ist. Rein. */
export function sperrGrund(stand: TeamStand | null, id: ZusatzId): string {
  const s = zusatzStand(stand, id);
  if (!s) return "";
  if (!s.notschalter) return "Am Server nicht erlaubt: der Notschalter ist aus.";
  if (!s.zugang) return "Am Server nicht eingerichtet: es gibt keinen aktiven Zugang.";
  return "";
}

/** Die Kontoschalter, die den Zustand bestimmen. */
export interface TeamKonto {
  /** Der Hauptschalter „Entwickler“ (Vorgabe an). */
  gesamt: boolean;
  entwicklerImChat: boolean;
  zimmermann: boolean;
  hueter: boolean;
}

/** Der Zustand aller drei — EINE Regel für Übersicht, Lage-Karte und diese Seite. Rein. */
export function teamZustaende(stand: TeamStand | null, konto: TeamKonto): Record<EntwicklerId, EntwicklerZustand> {
  const haupt = konto.gesamt && konto.entwicklerImChat;
  const schmied = entwicklerZustand({
    an: haupt,
    gesperrt: stand ? false : null,
    hoert: stand ? stand.schmiedHoertMit : null,
    ...(stand?.schmiedZuletztGesehenS !== undefined ? { zuletztS: stand.schmiedZuletztGesehenS } : {}),
  });
  const zusatz = (id: ZusatzId, eigen: boolean): EntwicklerZustand => {
    const s = zusatzStand(stand, id);
    return entwicklerZustand({
      an: haupt && eigen,
      gesperrt: s ? zusatzGesperrt(stand, id) : null,
      hoert: s?.hoert ?? null,
      ...(s?.zuletztGesehenS !== undefined ? { zuletztS: s.zuletztGesehenS } : {}),
    });
  };
  return { schmied, zimmermann: zusatz("zimmermann", konto.zimmermann), hueter: zusatz("hueter", konto.hueter) };
}

/** Die Kontoschalter aus den rohen Einstellungen (Konto 1). Rein. */
export function teamKontoAus(werte: Record<string, unknown>, uid: number | null | undefined): TeamKonto {
  return {
    gesamt: entwicklerGesamtAn(werte[ENTWICKLER_GESAMT_FELD], uid),
    entwicklerImChat: entwicklerImChatAn(werte[ENTWICKLER_IM_CHAT_FELD], uid),
    zimmermann: werte[entwickler("zimmermann").kontoFeld ?? ""] === true,
    hueter: werte[entwickler("hueter").kontoFeld ?? ""] === true,
  };
}

/** Der Satz rechts in der Übersichtszeile „Entwickler“: „Schmied hört zu · Zimmermann bereit · Hüter aus“. Rein. */
export function teamKurzText(stand: TeamStand | null, konto: TeamKonto): string {
  if (!stand) return "";
  const z = teamZustaende(stand, konto);
  return TEAM_IDS.map((id) => `${teamName(id)} ${ZUSTAND_WORT[z[id]]}`).join(" · ");
}

/** Der Erklärsatz zum Ansprechen — je Zusatz-Entwickler. Rein. */
export function zusatzSatz(id: ZusatzId): string {
  const e = entwickler(id);
  const anrede = `Ansprechen: „Hey ${e.name}, …“${e.rechnerName ? ` oder „${e.rechnerName}, …“` : ""} am Anfang, oder die Wahl „An: ${e.name}“ — dann antworten weder Vespera noch der Schmied. Nie eine Freigabe.`;
  if (id === "zimmermann") return `Die zweite Entwickler-Sitzung (${e.gebiet}) darf mitlesen und schreibt als Blase „${e.blasenName}“. ${anrede}`;
  return `Die Sitzung auf ${e.rechnerName ?? "einem eigenen Server"} (${e.gebiet}) schreibt als Blase „${e.blasenName}“. Sie sieht nur, was du an sie richtest, und die Beiträge des Teams — nie deine Gespräche mit Vespera. ${anrede}`;
}

/** Den Stand vom Server holen (`null`: nicht abrufbar) — auch für die Übersichtszeile. */
export const holeTeamStand = standVomServer;

/** Der Knopf „Probe hören“: spielt die Stimme mit dem Tempo, das GERADE für ihn gespeichert ist (Stimmenwahl und Tempo-Regler teilen ihn). */
function probeKnopf(id: EntwicklerId, k: EntwicklerSeiteKontext, stimme: () => string): HTMLButtonElement {
  const probe = document.createElement("button");
  probe.type = "button";
  probe.className = "btn btn--ghost einst-probe";
  probe.dataset.probe = id;
  const zeige = (z: ProbeZustand): void => {
    probe.dataset.state = z;
    probe.textContent = z === "loading" ? "Probe lädt …" : z === "playing" ? "Probe anhalten" : "Probe hören";
    probe.setAttribute("aria-busy", String(z === "loading"));
  };
  zeige("idle");
  probe.addEventListener("click", () => {
    if (probe.dataset.state === "loading") return;
    if (probe.dataset.state === "playing") {
      k.stoppeProbe();
      zeige("idle");
      return;
    }
    k.spieleProbe(stimme(), entwicklerTempoAus(id, k.werte()[entwickler(id).tempoFeld]), zeige);
  });
  return probe;
}

/** „1,05×“ — zwei Stellen, denn das Raster ist 0,05 und der Festwert des Schmieds 1,08. */
export function tempoGenau(w: number): string {
  return `${w.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
}

/**
 * Eine Reglerzeile: Name und Satz, der Wert, der Regler (`input[type=range]`, Pfeiltasten, `aria-valuetext`) und optional Knöpfe.
 * Ein Wert wird bei jeder Bewegung gespeichert (gebündelt, wie der Vorlese-Tempo-Regler). Rein bis auf das DOM.
 */
function reglerZeile(o: {
  feld: string;
  titel: string;
  satz: string;
  min: number;
  max: number;
  schritt: number;
  wert: number;
  text: (w: number) => string;
  aenderung: (w: number) => void;
  knoepfe?: HTMLElement[];
}): { zeile: HTMLElement; setze: (w: number) => void } {
  const zeile = document.createElement("div");
  zeile.className = "einst-feld einst-feld--gestapelt einst-regler";
  zeile.dataset.feld = o.feld;
  const anzeige = document.createElement("span");
  anzeige.className = "einst-regler-wert";
  const regler = document.createElement("input");
  regler.type = "range";
  regler.className = "einst-regler-eingabe";
  regler.min = String(o.min);
  regler.max = String(o.max);
  regler.step = String(o.schritt);
  regler.id = `einst-regler-${o.feld}`;
  regler.setAttribute("aria-label", o.titel);
  const setze = (w: number): void => {
    // Der Regler rastet auf sein Raster (1,08 → 1,10); der Text nennt immer den echten Wert.
    regler.value = String(w);
    anzeige.textContent = o.text(w);
    regler.setAttribute("aria-valuetext", o.text(w));
  };
  setze(o.wert);
  regler.addEventListener("input", () => {
    const w = Number(regler.value);
    anzeige.textContent = o.text(w);
    regler.setAttribute("aria-valuetext", o.text(w));
    o.aenderung(w);
  });
  const kopf = document.createElement("div");
  kopf.className = "einst-regler-kopf";
  kopf.append(einstText(o.titel, o.satz), anzeige);
  zeile.append(kopf, regler);
  if (o.knoepfe?.length) {
    const reihe = document.createElement("div");
    reihe.className = "einst-regler-knoepfe";
    reihe.append(...o.knoepfe);
    zeile.appendChild(reihe);
  }
  return { zeile, setze };
}

/** Das Tempo eines Entwicklers: Regler 0,8–1,6 (0,05), „Standard“ und die Probe. `null` im Konto = sein bisheriger Festwert. */
function entwicklerTempoZeile(id: EntwicklerId, k: EntwicklerSeiteKontext): HTMLElement {
  const e = entwickler(id);
  const titel = `Tempo des ${e.name}s`;
  const g = ENTWICKLER_TEMPO_GRENZE;
  const standard = document.createElement("button");
  standard.type = "button";
  standard.className = "btn btn--ghost einst-standard";
  standard.textContent = `Standard (${tempoGenau(e.tempo)})`;
  const gewaehlt = (): number | null => {
    const w = k.werte()[e.tempoFeld];
    return typeof w === "number" && entwicklerTempoAus(id, w) === w ? w : null;
  };
  const sync = (): void => {
    standard.disabled = gewaehlt() === null;
  };
  const r = reglerZeile({
    feld: e.tempoFeld,
    titel,
    satz: `Wie schnell der ${e.name} spricht, unabhängig von Vesperas Tempo. Standard ist ${tempoGenau(e.tempo)}.`,
    min: g.min,
    max: g.max,
    schritt: g.schritt,
    wert: entwicklerTempoAus(id, k.werte()[e.tempoFeld]),
    text: tempoGenau,
    aenderung: (w) => {
      const rein = Math.round(w * 100) / 100;
      k.speichere({ [e.tempoFeld]: Math.abs(rein - e.tempo) < 1e-9 ? null : rein }, "");
      sync();
    },
    knoepfe: [probeKnopf(id, k, () => entwicklerStimmeAus(id, k.werte()[e.stimmeFeld])), standard],
  });
  standard.addEventListener("click", () => {
    k.stoppeProbe();
    k.speichere({ [e.tempoFeld]: null }, `${titel} zurück auf Standard.`);
    r.setze(e.tempo);
    sync();
  });
  sync();
  return r.zeile;
}

/** „Ausführlichkeit“: kurz / normal / ausführlich. Nur ein Wunsch an den Mithör-Kopf des Entwicklers (Anzeige, kein Auftrag). */
function ausfuehrlichkeitZeile(id: EntwicklerId, k: EntwicklerSeiteKontext): HTMLElement {
  const e = entwickler(id);
  const zeile = document.createElement("div");
  zeile.className = "einst-feld einst-feld--gestapelt";
  zeile.dataset.feld = e.ausfuehrlichkeitFeld;
  const titel = `Ausführlichkeit des ${e.name}s`;
  const wahl = document.createElement("select");
  wahl.className = "einst-eingabe";
  wahl.id = `einst-ausfuehrlichkeit-${id}`;
  wahl.setAttribute("aria-label", titel);
  for (const a of AUSFUEHRLICHKEITEN) {
    const o = document.createElement("option");
    o.value = a.id;
    o.textContent = `${a.label}${a.id === AUSFUEHRLICHKEIT_VORGABE ? " (Vorgabe)" : ""}`;
    wahl.appendChild(o);
  }
  wahl.value = ausfuehrlichkeitAus(k.werte()[e.ausfuehrlichkeitFeld]);
  wahl.addEventListener("change", () => {
    const neu = ausfuehrlichkeitAus(wahl.value);
    k.speichere({ [e.ausfuehrlichkeitFeld]: neu === AUSFUEHRLICHKEIT_VORGABE ? null : neu }, `${titel} gespeichert.`);
  });
  zeile.append(einstText(titel, `Wie viel er schreibt. Er liest den Wunsch beim Mithören — eine Bitte von dir, kein Auftrag.`), wahl);
  return zeile;
}

/** „Stimme des …“: Auswahl aus SCHMIED_STIMMEN und eine Hörprobe mit seinem Tempo. */
function stimmeZeile(id: EntwicklerId, k: EntwicklerSeiteKontext): HTMLElement {
  const e = entwickler(id);
  const zeile = document.createElement("div");
  zeile.className = "einst-feld einst-feld--gestapelt";
  zeile.dataset.feld = e.stimmeFeld;
  const wahl = document.createElement("select");
  wahl.className = "einst-eingabe";
  wahl.id = `einst-stimme-${id}`;
  const titel = `Stimme des ${e.name}s`;
  wahl.setAttribute("aria-label", titel);
  for (const v of SCHMIED_STIMMEN) {
    const o = document.createElement("option");
    o.value = v.id;
    o.textContent = `${v.label}${v.id === e.stimmeVorgabe ? " (Vorgabe)" : ""}`;
    wahl.appendChild(o);
  }
  wahl.value = entwicklerStimmeAus(id, k.werte()[e.stimmeFeld]);
  wahl.addEventListener("change", () => {
    const neu = entwicklerStimmeAus(id, wahl.value);
    k.stoppeProbe();
    k.speichere({ [e.stimmeFeld]: neu === e.stimmeVorgabe ? null : neu }, `${titel} gespeichert.`);
  });

  const probe = probeKnopf(id, k, () => entwicklerStimmeAus(id, wahl.value));
  zeile.append(einstText(titel, `So klingt der ${e.name}, wenn er vorgelesen wird — mit seinem Tempo.`), wahl, probe);
  return zeile;
}

/** Die Seite. */
export function baueEntwicklerSeite(k: EntwicklerSeiteKontext): HTMLElement[] {
  if (!entwicklerImChatFuerKonto(k.uid)) {
    const satz = document.createElement("p");
    satz.className = "einst-seite-satz";
    satz.textContent = "Das Entwickler-Team gibt es nur im Hauptkonto.";
    return [satz];
  }
  const schmied = entwickler("schmied");

  /* ---- 1. Team: drei gleich gebaute Zeilen ---- */
  const konto: TeamKonto = teamKontoAus(k.werte(), k.uid);
  let stand: TeamStand | null = null;
  const team = einstAbschnitt("Team");
  team.abschnitt.classList.add("einst-team");
  const liste = document.createElement("ul");
  liste.className = "einst-team-liste";
  const hinweis = document.createElement("p");
  hinweis.className = "einst-feld-satz einst-team-aus";
  hinweis.id = "einst-team-aus";
  hinweis.setAttribute("role", "status");
  hinweis.setAttribute("aria-live", "polite");

  interface Zeile {
    li: HTMLElement;
    chip: HTMLElement;
    grund: HTMLElement;
  }
  const zeilen = new Map<EntwicklerId, Zeile>();

  const zeigeZustaende = (): void => {
    const z = teamZustaende(stand, konto);
    for (const id of TEAM_IDS) {
      const zeile = zeilen.get(id);
      if (!zeile) continue;
      zeile.li.dataset.zustand = z[id];
      zeile.chip.dataset.art = z[id];
      zeile.chip.replaceChildren(zustandsZeichen(z[id]), document.createTextNode(ZUSTAND_WORT[z[id]]));
      // Unter dem Namen: bei „nicht verbunden“ seit wann; bei „gesperrt“ warum (feste Sätze).
      const zuletzt = id === "schmied" ? stand?.schmiedZuletztGesehenS : zusatzStand(stand, id)?.zuletztGesehenS;
      const grund = z[id] === "getrennt" ? zuletztText(zuletzt) : id === "schmied" ? "" : sperrGrund(stand, id);
      zeile.grund.textContent = grund;
      zeile.grund.hidden = grund === "" || z[id] === "aus";
    }
    const hinweisText = !konto.gesamt
      ? "Hauptschalter aus: niemand vom Team kommt herein. Die Schalter darunter bleiben, wie sie sind."
      : !konto.entwicklerImChat
        ? "Aus: Zimmermann und Hüter kommen nur herein, wenn „Entwickler im Chat“ an ist."
        : "";
    hinweis.hidden = hinweisText === "";
    hinweis.textContent = hinweisText;
  };

  const zeileFuer = (id: EntwicklerId): HTMLElement => {
    const e = entwickler(id);
    const li = document.createElement("li");
    li.className = "einst-team-zeile";
    li.dataset.entwickler = id;
    const feld = id === "schmied" ? ENTWICKLER_IM_CHAT_FELD : (e.kontoFeld ?? "");
    li.dataset.feld = feld;
    const name = document.createElement("span");
    name.className = "einst-team-name";
    const titel = document.createElement("span");
    titel.className = "einst-team-titel";
    titel.textContent = e.name;
    const meta = document.createElement("span");
    meta.className = "einst-team-meta";
    const chip = document.createElement("span");
    chip.className = "einst-zustand-chip";
    chip.id = `einst-team-zustand-${id}`;
    const rolle = document.createElement("span");
    rolle.className = "einst-team-rolle";
    rolle.textContent = ROLLE[id];
    meta.append(chip, rolle);
    name.append(titel, meta);
    const schalterName = id === "schmied" ? "Entwickler im Chat" : `${e.name} im Chat`;
    const an = id === "schmied" ? konto.entwicklerImChat : id === "zimmermann" ? konto.zimmermann : konto.hueter;
    const schalter = schalterKnopf(schalterName, an, (neu) => {
      if (id === "schmied") konto.entwicklerImChat = neu;
      else konto[id] = neu;
      zeigeZustaende();
      const satz =
        id === "schmied"
          ? neu ? "Der Entwickler ist im Chat zugeschaltet." : "Der Entwickler ist nicht mehr im Chat."
          : neu ? `Der ${e.name} darf in den Chat.` : `Der ${e.name} ist nicht mehr im Chat.`;
      k.speichere({ [feld]: neu }, satz);
    });
    schalter.setAttribute("aria-describedby", chip.id);
    const grund = document.createElement("span");
    grund.className = "einst-team-grund";
    grund.hidden = true;
    li.append(name, schalter, grund);
    zeilen.set(id, { li, chip, grund });
    return li;
  };
  liste.append(...TEAM_IDS.map(zeileFuer));
  // Der Hauptschalter: alle drei zugleich — die Einzelschalter darunter ändert er nie.
  const gesamtSchalter = einstSchalter(
    "Entwickler",
    "Hauptschalter für alle drei. Aus: keiner kommt herein; die Schalter darunter bleiben, wie sie sind.",
    konto.gesamt,
    (an) => {
      konto.gesamt = an;
      zeigeZustaende();
      k.speichere({ [ENTWICKLER_GESAMT_FELD]: an }, an ? "Das Entwickler-Team ist eingeschaltet." : "Das Entwickler-Team ist ausgeschaltet.");
    },
    ENTWICKLER_GESAMT_FELD,
  ).zeile;
  gesamtSchalter.classList.add("einst-team-haupt");
  team.karte.append(gesamtSchalter, liste, hinweis);
  zeigeZustaende();

  /* ---- 2. Stimme und Ton: je Entwickler eingeklappt ---- */
  const ton = einstAbschnitt("Stimme und Ton");
  for (const id of TEAM_IDS) {
    const e = entwickler(id);
    const inhalt = document.createElement("div");
    inhalt.className = "einst-team-felder";
    inhalt.dataset.entwickler = id;
    const wie =
      id === "schmied"
        ? `Die Entwickler-Sitzung auf diesem Server. Seine Blase heißt „${schmied.blasenName}“.`
        : zusatzSatz(id);
    inhalt.append(stimmeZeile(id, k), entwicklerTempoZeile(id, k), ausfuehrlichkeitZeile(id, k), einstMehr(`Mehr zum ${e.name}`, wie));
    ton.karte.appendChild(einklappbar(e.name, inhalt, false));
  }

  /* ---- 3. Vorlesen: gilt für alle ---- */
  const vl = einstAbschnitt("Vorlesen");
  vl.karte.append(
    einstSchalter(
      "Entwickler vorlesen",
      "Neue Beiträge werden von selbst vorgelesen. Das gilt für den Schmied; Zimmermann und Hüter haben darunter einen eigenen Schalter (im Gespräch) und sonst den Knopf an ihrer Blase. Nie eine Freigabe.",
      entwicklerVorlesenAn(k.werte()[ENTWICKLER_VORLESEN_FELD], k.uid),
      (an) => {
        k.speichere({ [ENTWICKLER_VORLESEN_FELD]: an }, an ? "Die Entwickler werden vorgelesen." : "Die Entwickler werden nicht mehr vorgelesen.");
      },
      ENTWICKLER_VORLESEN_FELD,
    ).zeile,
    einstSchalter(
      "Zimmermann und Hüter im Gespräch vorlesen",
      "Spricht Martin gerade mit Zimmermann oder Hüter (oder hat Vespera ihnen übergeben), liest die App ihre Antwort mit ihrer eigenen Stimme von selbst vor. Berichte von Zimmermann und Hüter aus dem Hintergrund werden ebenfalls vorgelesen, außer reine Infos (Info-Meldungen). Während du aufnimmst, wartet das Vorlesen. Nie eine Freigabe.",
      zusatzVorlesenAn(k.werte()[ZUSATZ_VORLESEN_FELD], k.uid),
      (an) => {
        k.speichere({ [ZUSATZ_VORLESEN_FELD]: an }, an ? "Zimmermann und Hüter werden im Gespräch vorgelesen." : "Zimmermann und Hüter werden nicht mehr von selbst vorgelesen.");
      },
      ZUSATZ_VORLESEN_FELD,
    ).zeile,
    einstSchalter(
      "Beiträge in einem Stück vorlesen",
      "Aus: Stück für Stück (Stream) — der erste Satz kommt schnell. An: jeder Beitrag wird erst ganz vertont und dann in einem Stück gesprochen. Gilt für alle Entwickler. Nie eine Freigabe.",
      schmiedStimmeGanzAn(k.werte()[SCHMIED_GANZ_FELD], k.uid),
      (an) => {
        k.speichere({ [SCHMIED_GANZ_FELD]: an }, an ? "Die Entwickler sprechen jeden Beitrag in einem Stück." : "Die Entwickler werden wieder Stück für Stück gelesen.");
      },
      SCHMIED_GANZ_FELD,
    ).zeile,
  );

  /* ---- 4. Server: das Server-Netz, getrennt ---- */
  const sn = einstAbschnitt("Server");
  sn.karte.append(
    einstSchalter(
      "Server-Netz",
      "Liest alle 5 Minuten den Zustand von Server 8 (Platte, Speicher, Updates, Sicherung) und läutet die Glocke, wenn die Ampel rot wird. S6 und S7 melden in dieser Stufe noch nicht und stehen als „unbekannt“ da. Die Karte „Server“ oben in den Einstellungen erscheint erst, wenn auch der Notschalter SERVER_NETZ=1 am Server gesetzt ist. Nur lesen, nie eine Freigabe.",
      k.werte()[SERVER_NETZ_FELD] === true,
      (an) => {
        k.speichere({ [SERVER_NETZ_FELD]: an }, an ? "Das Server-Netz ist an." : "Das Server-Netz ist aus.");
      },
      SERVER_NETZ_FELD,
    ).zeile,
  );

  void (k.holeStand ?? standVomServer)()
    .catch(() => null)
    .then((s) => {
      stand = s;
      zeigeZustaende();
    });

  return [team.abschnitt, ton.abschnitt, vl.abschnitt, sn.abschnitt];
}
